import type { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState } from 'react';
import { useServices } from '@/contexts/app-services';
import { auth } from '@/services/auth';
import { localStorageService } from '@/services/local-storage';
import { logError } from '@/services/logger';
import { setCurrentUserId } from '@/state/auth-state';

type User = Pick<SupabaseUser, 'id'>;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const { analytics } = useServices();

  useEffect(() => {
    const identifyFromSession = (session: Session | null) => {
      if (session?.user.email) {
        analytics.identifyUser({
          userId: session.user.id,
          email: session.user.email,
        });
      }
    };

    const { data } = auth.onAuthStateChange((event, currentSession) => {
      identifyFromSession(currentSession);
      setCurrentUserId(currentSession?.user.id ?? null);
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setIsLoading(false);

      if (event === 'SIGNED_OUT') {
        queryClient.clear();
        analytics.trackSignedOut();
        analytics.resetIdentity();
        localStorageService.clearAllMessages();
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [analytics, queryClient]);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await auth.signIn(email, password);
      const sessionEmail = data.session?.user.email;
      if (!error && sessionEmail) {
        analytics.identifyUser({
          userId: data.session.user.id,
          email: sessionEmail,
        });
        analytics.trackSignedIn({ method: 'email' });
      }
      return { error: error as Error | null };
    } catch (error) {
      logError(error, 'Sign in failed');
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await auth.signUp(email, password);
      if (!error && data.user) {
        analytics.identifyUser({
          userId: data.user.id,
          email: data.user.email ?? email,
        });
        analytics.trackSignedUp({ method: 'email' });
      }
      return { error: error as Error | null };
    } catch (error) {
      logError(error, 'Sign up failed');
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await auth.signOut();
  };

  const value = {
    user,
    session,
    isLoading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
